// Strict numeric parser for KPI measurement `value` strings. Users type
// free-form values like "99.5%", "$12,500", "2h 30m", "42ms" — we only
// want to enable delta colouring when (a) we can confidently extract a
// number, and (b) consecutive measurements share the same unit.
//
// Rules:
// - Strip currency markers `[$£€,\s]` before regex.
// - Post-strip, reject if multi-number present (`\d…\D…\d`) — catches
//   "2h 30m", "4/5", "1/1/2026", "1.5e10" (the `e` separator makes it
//   two numbers).
// - Then require `/^-?\d+(\.\d+)?%?$/` — rejects hex (`0x1F`), NaN,
//   European decimals (`1,5`), bare scientific (caught above).
// - On accept, `unit = '%'` when the stripped value ends with `%`,
//   else empty string.
// - On reject, `unit = raw` — preserved so consumers can detect unit
//   changes between measurements (e.g. "99.5%" → "99500" flips unit
//   from "%" to "" and triggers a unit-change guard).

export interface ParsedMeasurement {
  numeric: number | null;
  unit: string;
}

const CURRENCY_STRIP = /[$£€,\s]/g;
const MULTI_NUMBER = /\d[^0-9.\-%]+\d/;
const NUMERIC_REGEX = /^-?\d+(\.\d+)?%?$/;

export function parseKpiMeasurementValue(
  raw: string | null | undefined,
): ParsedMeasurement {
  if (!raw) return { numeric: null, unit: "" };
  const stripped = raw.replace(CURRENCY_STRIP, "");
  if (!stripped) return { numeric: null, unit: raw };
  if (MULTI_NUMBER.test(stripped)) return { numeric: null, unit: raw };
  if (!NUMERIC_REGEX.test(stripped)) return { numeric: null, unit: raw };
  const hasPct = stripped.endsWith("%");
  const numberPart = hasPct ? stripped.slice(0, -1) : stripped;
  const numeric = parseFloat(numberPart);
  if (!Number.isFinite(numeric)) return { numeric: null, unit: raw };
  return { numeric, unit: hasPct ? "%" : "" };
}
